// S2-LIB3: DonutChart
// Accessible, token-styled donut chart with optional clickable legend,
// animated segment reveal, and graceful empty state. Pure SVG — no
// third-party chart lib, so it stays tree-shakeable and matches the
// visual spec in docs/sprint-2/wireframes/s2-cc6-hero-and-section3.html
// exactly (see the `.donut-*` classes there for cross-reference).
//
// Why a custom component instead of Recharts:
//  - Exact token-driven visuals (no inline-style leakage).
//  - Staggered animation is trivial with plain CSS transitions on the
//    stroke-dasharray of each <circle>.
//  - Empty state + click/keyboard affordances are easier to spec here.

import { useEffect, useMemo, useRef, useState } from 'react';
import './DonutChart.css';

/** A single slice of the donut. `value` is raw (not a %); component
 *  computes percentages so callers can pass any units (cost, count, etc). */
export interface DonutSegment {
  id: string;
  label: string;
  value: number;
  /** CSS value for stroke — e.g. `var(--chart-1)` or a hex. If unset,
   *  we fall back to the chart-N palette in order. */
  color?: string;
}

export interface DonutChartProps {
  segments: DonutSegment[];
  /** Big number displayed in the donut's hole (e.g. "47.3¢"). */
  centerValue?: string;
  /** Small muted sub-label under the center value (e.g. "today"). */
  centerLabel?: string;
  /** Overall SVG size in px. Default 180. */
  size?: number;
  /** Stroke thickness of the ring in px. Default 22. */
  ringThickness?: number;
  /** Show the legend below the SVG. Default true. */
  showLegend?: boolean;
  /** If provided, legend rows become clickable + keyboard-activatable. */
  onSegmentClick?: (seg: DonutSegment) => void;
  /** Shown inside the ring when there's no data. Default "No data". */
  emptyMessage?: string;
}

// Fallback palette used when a segment has no `color`. Indices wrap
// modulo 8 so any number of segments still renders.
const FALLBACK_PALETTE = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];

// Resolve the stroke color for a segment — explicit color if provided,
// otherwise the next slot in the fallback palette.
function resolveColor(seg: DonutSegment, index: number): string {
  return seg.color ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

// Number formatter for legend values — at most one decimal place.
// Declared once so we don't re-allocate on every render.
const legendValueFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
});

/** Per-segment geometry pre-computed once per `segments` change. */
interface SegmentGeometry {
  seg: DonutSegment;
  index: number;
  color: string;
  pct: number;       // 0–100
  length: number;    // arc length in SVG user units
  offset: number;    // cumulative offset before this slice
}

export function DonutChart({
  segments,
  centerValue,
  centerLabel,
  size = 180,
  ringThickness = 22,
  showLegend = true,
  onSegmentClick,
  emptyMessage = 'No data',
}: DonutChartProps) {
  // -- Geometry ----------------------------------------------------
  // R is chosen so the stroke stays inside the SVG box with a 2px
  // breathing margin (matches the wireframe: size 180, R = 70).
  const R = size / 2 - ringThickness / 2 - 2;
  const C = 2 * Math.PI * R;

  // Total of all values — drives the percentage calculation. If zero,
  // we're in empty state (no slices to render).
  const total = useMemo(
    () => segments.reduce((sum, s) => sum + (s.value || 0), 0),
    [segments]
  );
  const isEmpty = segments.length === 0 || total === 0;

  // Pre-computed geometry for every non-empty segment. Memoized so the
  // animation effect below has stable identity to compare against.
  const geometry = useMemo<SegmentGeometry[]>(() => {
    if (isEmpty) return [];
    let cumulative = 0;
    return segments.map((seg, index) => {
      const pct = (seg.value / total) * 100;
      const length = (seg.value / total) * C;
      const geom: SegmentGeometry = {
        seg,
        index,
        color: resolveColor(seg, index),
        pct,
        length,
        offset: cumulative,
      };
      cumulative += length;
      return geom;
    });
  }, [segments, total, C, isEmpty]);

  // -- Animation ---------------------------------------------------
  // We render every segment with dasharray "0 C" on first paint, then
  // flip to the real dasharray on the next animation frame. CSS picks up
  // the change and animates via the `transition` on `.airgent-donut-seg`.
  // The per-segment `transitionDelay` gives the staggered reveal.
  //
  // We identify "which segment set is this" by hashing segment ids — when
  // the caller passes a new segments array with different ids, we replay
  // the animation from zero.
  const segmentsKey = useMemo(
    () => segments.map((s) => s.id).join('|'),
    [segments]
  );
  const [animated, setAnimated] = useState(false);
  const prevKeyRef = useRef<string>('');

  useEffect(() => {
    // On first mount AND on segments-id-set change, restart from zero.
    if (prevKeyRef.current !== segmentsKey) {
      prevKeyRef.current = segmentsKey;
      setAnimated(false);
      // Defer to next frame so the browser paints the 0-length state,
      // then transitions to the real lengths.
      const raf = requestAnimationFrame(() => setAnimated(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [segmentsKey]);

  // -- Render ------------------------------------------------------
  const center = size / 2;

  return (
    <div className="airgent-donut-wrap">
      <div
        className="airgent-donut-svg-wrap"
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={isEmpty ? emptyMessage : 'Donut chart'}
        >
          {/* Track ring — always rendered, provides the background
              circle even in empty state. */}
          <circle
            cx={center}
            cy={center}
            r={R}
            fill="none"
            stroke="var(--surface-1)"
            strokeWidth={ringThickness}
            data-testid="donut-track"
          />
          {/* Foreground segments — only when we have data. */}
          {!isEmpty &&
            geometry.map((g) => {
              // Pre-animation: length 0 (invisible). Post-animation: real length.
              const dashLen = animated ? g.length : 0;
              return (
                <circle
                  key={g.seg.id}
                  className="airgent-donut-seg"
                  data-testid="donut-seg"
                  data-seg-id={g.seg.id}
                  cx={center}
                  cy={center}
                  r={R}
                  fill="none"
                  stroke={g.color}
                  strokeWidth={ringThickness}
                  strokeDasharray={`${dashLen} ${C - dashLen}`}
                  strokeDashoffset={-g.offset}
                  style={{ transitionDelay: `${g.index * 100}ms` }}
                />
              );
            })}
        </svg>

        {/* Center label — either (value + sub-label) or empty message. */}
        <div className="airgent-donut-center">
          {isEmpty ? (
            <div className="airgent-donut-center-empty">{emptyMessage}</div>
          ) : (
            <>
              {centerValue && (
                <div className="airgent-donut-center-val">{centerValue}</div>
              )}
              {centerLabel && (
                <div className="airgent-donut-center-sub">{centerLabel}</div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Legend — hidden in empty state or when showLegend=false. */}
      {showLegend && !isEmpty && (
        <div className="airgent-donut-legend">
          {geometry.map((g) => (
            <LegendRow
              key={g.seg.id}
              geom={g}
              onSegmentClick={onSegmentClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Single legend row — extracted so the click/keyboard affordances stay
// isolated and the main component reads cleanly.
function LegendRow({
  geom,
  onSegmentClick,
}: {
  geom: SegmentGeometry;
  onSegmentClick?: (seg: DonutSegment) => void;
}) {
  const clickable = !!onSegmentClick;
  // Round to 1dp for the shown pct — keeps totals close to 100 even with
  // many tiny slices, and matches the wireframe visuals.
  const pctDisplay = Math.round(geom.pct * 10) / 10;
  const valueDisplay = legendValueFormatter.format(geom.seg.value);

  // Handler factory — only attaches when onSegmentClick is provided so
  // non-interactive legends don't expose button semantics.
  const handleClick = clickable
    ? () => onSegmentClick!(geom.seg)
    : undefined;

  // Keyboard: Enter + Space activate the row, matching native button
  // semantics. We prevent default on Space to avoid page-scroll.
  const handleKeyDown = clickable
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSegmentClick!(geom.seg);
        }
      }
    : undefined;

  return (
    <div
      className={
        'airgent-donut-legend-row' + (clickable ? ' is-clickable' : '')
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-seg-id={geom.seg.id}
    >
      <div
        className="airgent-donut-legend-swatch"
        style={{ background: geom.color }}
      />
      <span className="airgent-donut-legend-name">{geom.seg.label}</span>
      <span className="airgent-donut-legend-pct">
        {pctDisplay}% · {valueDisplay}
      </span>
    </div>
  );
}
